import {
  BoxGeometry,
  CanvasTexture,
  CylinderGeometry,
  Group,
  Mesh,
  MeshLambertMaterial,
  SphereGeometry,
  type BufferGeometry,
  type Material,
  type Object3D,
} from 'three';
import { JUMP_ZONE } from '../../data/tuning';
import { t } from '../../i18n/es';
import { LIVERY } from '../themes/livery';

/**
 * Props of El Salto (GDD §11.1, §3.4, issue #94), low-poly with flat shading: the border sign (its text is drawn in
 * code on a canvas, no external fonts), the X of the target (two flat crossed beams on the ground, 3 u across, with a
 * marked centre), Turbina falling with La Bolsa, her parachute and her standing landed pose. Nobody crashes.
 */
export interface JumpProps {
  sign: Group;
  target: Group;
  pilotFalling: Group;
  parachute: Group;
  pilotLanded: Group;
  dispose(): void;
}

/** Colours of the props: playable saturation for the X, a plain blue board for the sign (never the critical red). */
export const JUMP_COLORS = { board: 0x1f3f78, boardText: 0xf2f4f7, post: 0x8a8f96, beam: 0xf2c230, centre: 0xf6f1d8, skin: 0xe0b48c, suit: LIVERY.blue, helmet: LIVERY.yellow, canopy: 0xf2c230 } as const;

export function buildJumpProps(): JumpProps {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const textures: CanvasTexture[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const add = <G extends BufferGeometry>(parent: Object3D, geometry: G, material: Material, at: [number, number, number]) => {
    geometries.push(geometry);
    const mesh = new Mesh(geometry, material);
    mesh.position.set(...at);
    parent.add(mesh);
    return mesh;
  };

  // The border sign: a board on two posts, the text on a canvas texture.
  const sign = new Group();
  sign.name = 'border-sign';
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 128;
  const context = canvas.getContext('2d');
  if (context) {
    context.fillStyle = '#1f3f78';
    context.fillRect(0, 0, 512, 128);
    context.fillStyle = '#f2f4f7';
    context.font = 'bold 44px ui-monospace, Menlo, Consolas, monospace';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(t('world.sign.usa'), 256, 64);
  }
  const texture = new CanvasTexture(canvas);
  textures.push(texture);
  const faceMaterial = new MeshLambertMaterial({ map: texture, flatShading: true });
  materials.push(faceMaterial);
  geometries.push(new BoxGeometry(6, 1.5, 0.15));
  const board = new Mesh(geometries[geometries.length - 1]!, [paint(JUMP_COLORS.board), paint(JUMP_COLORS.board), paint(JUMP_COLORS.board), paint(JUMP_COLORS.board), faceMaterial, paint(JUMP_COLORS.board)]);
  board.position.set(0, 3.2, 0);
  sign.add(board);
  for (const x of [-2.4, 2.4]) add(sign, new CylinderGeometry(0.08, 0.08, 3.2, 6), paint(JUMP_COLORS.post), [x, 1.6, 0]);
  sign.userData.label = t('world.sign.usa');
  sign.visible = false;

  // The X: two flat beams crossed on the ground and a bright centre post (the centre is what PERFECTO means).
  const target = new Group();
  target.name = 'jump-target-x';
  const span = JUMP_ZONE.targetSpan;
  for (const angle of [Math.PI / 4, -Math.PI / 4]) {
    const beam = add(target, new BoxGeometry(span, 0.06, 0.28), paint(JUMP_COLORS.beam), [0, 0.03, 0]);
    beam.rotation.y = angle;
  }
  add(target, new CylinderGeometry(0.12, 0.12, 0.7, 8), paint(JUMP_COLORS.centre), [0, 0.35, 0]);
  target.visible = false;

  // Turbina falling with La Bolsa.
  const pilotFalling = new Group();
  pilotFalling.name = 'pilot-falling';
  add(pilotFalling, new BoxGeometry(0.24, 0.34, 0.18), paint(JUMP_COLORS.suit), [0, 0, 0]);
  add(pilotFalling, new SphereGeometry(0.12, 6, 4), paint(JUMP_COLORS.skin), [0, 0.27, 0]);
  add(pilotFalling, new SphereGeometry(0.135, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2), paint(JUMP_COLORS.helmet), [0, 0.3, 0]);
  const bag = add(pilotFalling, new BoxGeometry(0.3, 0.3, 0.22), paint(LIVERY.bag), [0.28, -0.05, 0]);
  bag.name = 'pilot-bag';
  pilotFalling.visible = false;

  // The parachute opens above her.
  const parachute = new Group();
  parachute.name = 'pilot-parachute';
  add(parachute, new SphereGeometry(0.8, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), paint(JUMP_COLORS.canopy), [0, 0.95, 0]);
  for (const x of [-0.5, 0.5]) {
    const line = add(parachute, new CylinderGeometry(0.012, 0.012, 0.9, 3), paint(LIVERY.rope), [x * 0.6, 0.45, 0]);
    line.rotation.z = x > 0 ? -0.4 : 0.4;
  }
  parachute.visible = false;

  // Landed: standing next to La Bolsa, the canopy a soft heap beside her.
  const pilotLanded = new Group();
  pilotLanded.name = 'pilot-landed';
  add(pilotLanded, new BoxGeometry(0.24, 0.34, 0.18), paint(JUMP_COLORS.suit), [0, 0.45, 0]);
  add(pilotLanded, new SphereGeometry(0.12, 6, 4), paint(JUMP_COLORS.skin), [0, 0.72, 0]);
  add(pilotLanded, new SphereGeometry(0.135, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2), paint(JUMP_COLORS.helmet), [0, 0.75, 0]);
  add(pilotLanded, new BoxGeometry(0.1, 0.28, 0.1), paint(JUMP_COLORS.suit), [0, 0.14, 0]);
  add(pilotLanded, new BoxGeometry(0.3, 0.3, 0.22), paint(LIVERY.bag), [0.4, 0.15, 0]);
  add(pilotLanded, new SphereGeometry(0.4, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2), paint(JUMP_COLORS.canopy), [-0.6, 0, 0]).scale.set(1.2, 0.35, 0.8);
  pilotLanded.visible = false;

  return {
    sign,
    target,
    pilotFalling,
    parachute,
    pilotLanded,
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
      for (const texture of textures) texture.dispose();
    },
  };
}
